import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { Team } from "@redotech/redo-team-common/team";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { Flex } from "@redotech/redo-web/flex";
import { SettingsControlRow } from "@redotech/redo-web/settings-rows/settings-control-row";
import {
  SettingsDropdownRow,
  settingsTextOption,
} from "@redotech/redo-web/settings-rows/settings-dropdown-row";
import { SettingsGroup } from "@redotech/redo-web/settings-rows/settings-group";
import { SettingsNoteRow } from "@redotech/redo-web/settings-rows/settings-note-row";
import { SettingsStaticRow } from "@redotech/redo-web/settings-rows/settings-static-row";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import {
  assignInsightSlot,
  qualifySupportInsights,
  resolveInsightSlots,
  supportInsightDisabledReason,
  supportInsightIds,
  supportInsightLabels,
  supportInsightSignalsFromTeam,
  type SupportInsightId,
} from "./qualify-support-insights";
import {
  supportInsightLocationHasCopy,
  supportInsightMessage,
} from "./support-insight-figures";
import type { SupportInsightLocationId } from "./support-insight-locations";
import {
  assignInsightLocation,
  defaultInsightLocations,
  insightLocationsUsedElsewhere,
  supportInsightLocations,
} from "./support-insight-locations";

const TEAMS_PAGE_BANNER_SLOT = 3;
const TEAMS_PAGE_BANNER_LOCATION = "Banner on Teams page";

const locationOptions: RedoListItem<SupportInsightLocationId>[] =
  supportInsightLocations.map((location) =>
    "recommended" in location
      ? {
          id: location.id,
          infoBadge: { color: "brand", text: "Recommended" },
          text: location.label,
          type: "text",
          value: location.id,
        }
      : {
          id: location.id,
          text: location.label,
          type: "text",
          value: location.id,
        },
  );

const actionOptions = supportInsightIds.map((id) =>
  settingsTextOption(id, supportInsightLabels[id]),
);

export const SupportInsights = memo(function SupportInsights({
  team,
}: {
  team: Team;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const metricsLoad = useLoad(
    (signal) =>
      rpcClient.getSupportInsightMetrics({ teamId: team._id }, { signal }),
    [rpcClient, team._id],
  );
  const signals = supportInsightSignalsFromTeam(team);
  const qualified = qualifySupportInsights(signals);
  const [overrides, setOverrides] = useState<
    (SupportInsightId | null)[] | null
  >(null);
  const [locationOverrides, setLocationOverrides] = useState<
    SupportInsightLocationId[] | null
  >(null);
  const slots = resolveInsightSlots(qualified, overrides);
  const locations = locationOverrides ?? defaultInsightLocations();
  const disabledActions = supportInsightIds.flatMap((id) => {
    const tooltip = supportInsightDisabledReason(id, signals);
    return tooltip ? [{ value: id, tooltip }] : [];
  });

  return (
    <Flex dir="column" gap="xl" w="full">
      {qualified.length === 0 ? (
        <SettingsNoteRow>
          This merchant does not qualify for an insight. Support, the chat
          widget, and the AI channels these actions cover are already
          configured.
        </SettingsNoteRow>
      ) : null}
      {slots.map((slotId, index) => {
        const location = locations[index] ?? null;
        const teamsPageBanner = index === TEAMS_PAGE_BANNER_SLOT;
        const messageLocation = teamsPageBanner
          ? "teams-page-banner"
          : location;
        const countedLocation =
          messageLocation !== null &&
          supportInsightLocationHasCopy(messageLocation);
        const volume = metricsLoad.value;
        const message =
          slotId && countedLocation && volume
            ? supportInsightMessage(messageLocation, slotId, signals, volume)
            : undefined;
        const totalsReady =
          metricsLoad.value !== undefined || metricsLoad.error !== undefined;
        const preview =
          !slotId || !countedLocation || (volume && !message)
            ? undefined
            : (message ??
              (totalsReady
                ? "Ticket totals are unavailable for this merchant."
                : "Loading this merchant's ticket totals."));
        return (
          <SettingsGroup heading={`Insight ${index + 1}`} key={index}>
            {teamsPageBanner ? (
              <SettingsControlRow controlWidth="content" title="Location">
                <Text fontSize="xs" fontWeight="medium" textColor="primary">
                  {TEAMS_PAGE_BANNER_LOCATION}
                </Text>
              </SettingsControlRow>
            ) : (
              <SettingsDropdownRow<SupportInsightLocationId>
                disabledOptions={insightLocationsUsedElsewhere(
                  locations,
                  index,
                )}
                options={locationOptions}
                setValue={(value) => {
                  setLocationOverrides(
                    assignInsightLocation(locations, index, value),
                  );
                }}
                title="Location"
                value={location}
              />
            )}
            <SettingsDropdownRow<SupportInsightId>
              disabledOptions={disabledActions}
              options={actionOptions}
              placeholder="Choose an insight"
              setValue={(value) => {
                setOverrides(assignInsightSlot(slots, index, value));
              }}
              title="Action"
              value={slotId}
            />
            {preview ? (
              <SettingsStaticRow title="Insight preview" value={preview} />
            ) : qualified.length > 0 && !slotId ? (
              <SettingsNoteRow>Pick an insight for this spot.</SettingsNoteRow>
            ) : null}
          </SettingsGroup>
        );
      })}
    </Flex>
  );
});
